/**
 * Logo.tsx — Store Logo (Magento-Connected)
 * 
 * Reads logo from Magento storeConfig (Content > Design > Configuration > Header).
 * Constructs full URL using base_media_url + 'logo/' + header_logo_src.
 * Falls back to a branded icon + store name if no logo is configured.
 */
import React from 'react';
import { getOptimizedImageUrl, webpFallback } from '../../../utils/image';

interface LogoData {
  src?: string;
  darkSrc?: string;
  alt?: string;
  text?: string;
  width?: number | string;
  /** Logo render height in px — the merchant-facing size control (desktop). */
  height?: number | string;
  /** Logo render height in px on mobile (<1024px). Defaults to `height` when
   *  unset, so existing layouts are unchanged; set larger so the logo reads well
   *  on a narrow header without growing the desktop row. */
  mobileHeight?: number | string;
  size?: string;
  maxHeight?: number | string;
  contextPosition?: string;
  type?: string;
}

interface StoreConfigShape {
  store_name?: string;
  logo_alt?: string;
  header_logo_src?: string;
  base_media_url?: string;
}

interface CmsBlockShape {
  identifier?: string;
  content?: string;
}

interface SiteContentShape {
  siteName?: string;
  logo?: {
    alt?: string;
    width?: number | string;
  };
}

interface LogoContext {
  storeConfig?: StoreConfigShape;
  siteContent?: SiteContentShape;
  cmsBlocks?: CmsBlockShape[];
}

interface LogoProps {
  data: LogoData;
  styles?: string;
  context?: LogoContext;
}

export const Logo: React.FC<LogoProps> = ({ data, styles, context }) => {
  const storeConfig = context?.storeConfig;
  const siteContent = context?.siteContent;

  // Variant: image-only | text-only | image-text | dual-mode (default: image-only)
  const variantRaw = (data as { variant?: string } | undefined)?.variant
    ?? (data as { content?: { variant?: string } } | undefined)?.content?.variant
    ?? 'image-only';
  const variant: 'image-only' | 'text-only' | 'image-text' | 'dual-mode' =
    (typeof variantRaw === 'string' && ['image-only','text-only','image-text','dual-mode'].includes(variantRaw))
      ? variantRaw as 'image-only' | 'text-only' | 'image-text' | 'dual-mode'
      : 'image-only';

  const storeName = data?.text || siteContent?.siteName || storeConfig?.store_name || '';
  const logoAlt = data?.alt || siteContent?.logo?.alt || storeConfig?.logo_alt || storeName || 'Store Logo';

  const baseMedia = storeConfig?.base_media_url || '/media/';

  // Helper: Extract image src from CMS block HTML
  const extractLogoFromCmsBlock = (identifier: string) => {
    const cmsBlocks = context?.cmsBlocks ?? [];
    const block = cmsBlocks.find((b) => b.identifier === identifier);
    if (block?.content) {
      const decoded = block.content.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
      const match = decoded.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (match?.[1]) {
        const src = match[1];
        if (src.includes('{{media url=')) {
          const urlMatch = src.match(/{{media url=["']?([^"'}]+)["']?}}/i);
          if (urlMatch?.[1]) {
            return `${baseMedia}${urlMatch[1]}`.replace(/\/\.renditions\//g, '/');
          }
        }
        return src.replace(/\/\.renditions\//g, '/');
      }
    }
    return null;
  };

  const isFooter = data?.contextPosition === 'footer' || data?.type === 'logo_footer';

  // Priority 1: CMS Blocks (logo_nav_light = shown in Light Mode, logo_nav_dark = shown in Dark Mode)
  let logoSrc = extractLogoFromCmsBlock(isFooter ? 'logo_footer_light' : 'logo_nav_light');
  let darkLogoSrc = extractLogoFromCmsBlock(isFooter ? 'logo_footer_dark' : 'logo_nav_dark');

  // Priority 2: Direct Admin Editor Inputs (data.src / data.darkSrc)
  if (!logoSrc && data?.src) {
    logoSrc = data.src;
    if (!logoSrc.startsWith('http') && !logoSrc.startsWith('/')) {
      logoSrc = `${baseMedia}logo/${logoSrc}`;
    }
  }
  if (!darkLogoSrc && data?.darkSrc) {
    darkLogoSrc = data.darkSrc;
    if (!darkLogoSrc.startsWith('http') && !darkLogoSrc.startsWith('/')) {
      darkLogoSrc = `${baseMedia}logo/${darkLogoSrc}`;
    }
  }

  // Priority 3: Magento Admin Design Configuration
  if (!logoSrc && storeConfig?.header_logo_src) {
    const logoFile = storeConfig.header_logo_src;
    logoSrc = logoFile.startsWith('http') ? logoFile : `${baseMedia}logo/${logoFile}`;
  }

  // Logo sizing (one coherent control)
  // A merchant sets ONE value in the editor — the Logo block `height` field.
  // We render the logo at exactly that pixel height with width:auto, so the
  // aspect ratio is preserved, and cap max-width (from the `width` field) so a
  // very wide logo can't push the header out. `object-contain` means an
  // over-large uploaded image scales DOWN to fit rather than blowing out — so
  // any upload looks right with no further tweaking.
  //
  // CRITICAL: height is applied as an INLINE STYLE (not a Tailwind class) so it
  // wins the cascade over the global CLS rule
  // `img[width][height] { height: auto }` (Layout.astro) by inline specificity —
  // no `!important` needed (project rule). The old class-based `h-12 lg:h-16`
  // lost to that rule and collapsed the logo.
  const toPx = (v: number | string | undefined, fallback: number): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
  const logoHeightPx = toPx(data?.height ?? data?.maxHeight, 48);
  // Mobile height (<1024px) — defaults to the desktop height so existing layouts
  // are byte-for-byte unchanged until a merchant sets it.
  const logoMobileHeightPx = toPx(data?.mobileHeight, logoHeightPx);
  const logoMaxWidthPx = toPx(data?.width ?? siteContent?.logo?.width, 300);
  // Responsive height WITHOUT a specificity fight or !important: the img keeps an
  // INLINE `height: var(--logo-h)` (inline beats the global CLS rule
  // `img[width][height]{height:auto}`), and we change the INHERITED `--logo-h`
  // per breakpoint via a scoped <style>. A deterministic class keyed on the two
  // px values keeps SSR + client identical (no #418); two logos of the same size
  // simply share one harmless rule.
  const logoSizeClass = `logo-sz-${logoHeightPx}-${logoMobileHeightPx}`;
  const responsiveHeight = logoMobileHeightPx !== logoHeightPx;
  const imgSizeStyle: React.CSSProperties = {
    height: `var(--logo-h, ${logoHeightPx}px)`,
    width: 'auto',
    maxWidth: `${logoMaxWidthPx}px`,
    objectFit: 'contain',
  };

  return (
    /*
     * FIX: removed `h-full` from <a> and the inner <div>. The parent BlockRenderer
     * wrapper has no explicit pixel height, so height:100% was collapsing to 0px,
     * making the <img> render at 0px tall (downloaded but invisible). The image
     * needs to control its own height via h-10/lg:h-12.
     */
    <a href="/" className={`flex items-center ${logoSizeClass} ${styles ?? ''}`} aria-label={logoAlt} data-logo-variant={variant}>
      {/* Per-breakpoint logo height. Desktop sets --logo-h; the mobile media query
          overrides it (only emitted when the two differ, so default layouts add
          no CSS). The img reads it via its inline height. */}
      <style dangerouslySetInnerHTML={{ __html: `
        .${logoSizeClass}{--logo-h:${logoHeightPx}px;}
        ${responsiveHeight ? `@media (max-width:1023px){.${logoSizeClass}{--logo-h:${logoMobileHeightPx}px;}}` : ''}
      `}} />
      <div className="flex items-center gap-2 group cursor-pointer">
        {/* Text-only variant: skip image entirely, render wordmark only. */}
        {variant === 'text-only' && storeName && (
          <span className="font-black text-xl tracking-widest uppercase text-[var(--color-header-brand-text,var(--color-header-menu-text,var(--text-primary)))] group-hover:text-[var(--color-header-icon-hover,var(--color-secondary))] transition-colors">
            {storeName}
          </span>
        )}
        {variant !== 'text-only' && logoSrc ? (
          <>
            {/*
             * Theme-switching CSS. Project rule (iron-clad): no `!important`.
             * We win the cascade via SELECTOR SPECIFICITY — `html.dark a .logo-theme-light`
             * is 0,2,2 (one class on html + element-anchor + class on img), which beats
             * any plain `.something { display: ... }` rule. The class targets are
             * scoped under the anchor element to keep collisions minimal.
             */}
            {darkLogoSrc && (
              <style dangerouslySetInnerHTML={{ __html: `
                html.dark a .logo-theme-light { display: none; }
                html:not(.dark) a .logo-theme-dark { display: none; }
                html.light a .logo-theme-dark { display: none; }
              `}} />
            )}
            <img
              src={getOptimizedImageUrl(logoSrc, 210, 60)}
              srcSet={`${getOptimizedImageUrl(logoSrc, 210, 60)} 1x, ${getOptimizedImageUrl(logoSrc, 420, 60)} 2x`}
              alt={logoAlt}
              width={Math.round(logoMaxWidthPx)}
              height={Math.round(logoHeightPx)}
              /*
               * explicit width/height eliminate the Lighthouse CLS warning + reserve layout space.
               * sizes tuned to actual displayed dimensions reported by Lighthouse (210x70 desktop).
               * DPR-aware srcSet: 210w covers 1x; 420w covers 2x (retina). q=60 for the logo
               * since flat-art assets are tolerant of aggressive compression — saves another ~5 KiB.
               * width=210 aligns with the @1x source so the intrinsic ratio stays 210:70 (3:1).
               * fetchPriority=high added. The header logo
               * sits above the fold on every storefront page; on mobile viewports
               * where the product gallery doesn't enter view until after scroll,
               * Lighthouse picks the logo as the LCP element. Without
               * fetchPriority=high, the browser ranks it as a normal-priority
               * image (default for <img> tags below the LCP preload) — costing
               * ~150-300ms in cold-mobile LCP. Same hint applied to the dark
               * variant below.
               */
              className={`object-contain ${darkLogoSrc ? 'logo-theme-light' : ''}`}
              style={{ background: 'transparent', padding: '2px 0', ...imgSizeStyle }}
              fetchPriority="high"
              loading="eager"
              decoding="sync"
              onError={webpFallback(logoSrc)}
            />
            {darkLogoSrc && (
              <img
                src={getOptimizedImageUrl(darkLogoSrc, 210, 60)}
                srcSet={`${getOptimizedImageUrl(darkLogoSrc, 210, 60)} 1x, ${getOptimizedImageUrl(darkLogoSrc, 420, 60)} 2x`}
                alt={logoAlt}
                width={Math.round(logoMaxWidthPx)}
                height={Math.round(logoHeightPx)}
                className="object-contain logo-theme-dark"
                style={{ background: 'transparent', padding: '2px 0', ...imgSizeStyle }}
                fetchPriority="high"
                loading="eager"
                decoding="sync"
                onError={webpFallback(darkLogoSrc)}
              />
            )}
          </>
        ) : (
          <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-[var(--accent-primary)] to-[#22c55e] shadow-[0_0_15px_rgba(57,255,20,0.4)]" style={{ borderRadius: 'var(--style-btn-radius, 8px)' }}>
            <svg className="w-5 h-5 text-[var(--color-button-text,#ffffff)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        )}
        {/* Wordmark — shown when:
            - variant is `image-text` (always show alongside the image)
            - OR no logo image is available AND storeName exists (fallback) */}
        {variant !== 'text-only' && (variant === 'image-text' || !logoSrc) && storeName && storeName !== 'Default Store View' && (
          <span className="hidden md:inline-block font-black text-xl tracking-widest uppercase text-[var(--color-header-brand-text,var(--color-header-menu-text,var(--text-primary)))] group-hover:text-[var(--color-header-icon-hover,var(--color-secondary))] transition-colors">
            {storeName}
          </span>
        )}
      </div>
    </a>
  );
};

export default Logo;
